import React, {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Popper,
    Stack,
    Typography,
} from "@mui/material";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import CachedIcon from "@mui/icons-material/Cached";
import CakeOutlined from "@mui/icons-material/CakeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StarIcon from "@mui/icons-material/Star";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {getProfileFullNameReversed} from "crqlar-react-common/dist/utils/profile.util";
import {useService} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {labelizeUnderScoreText, useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {
    getContrastingColor,
    getEventColorBasedOnStatus
} from "crqlar-react-common/dist/utils/color.util";
import {AgeCategoriesElement} from "../../../../common/uiElements/AgeCategoriesElement";
import {
    ALLERGY_ICON_MAP,
    getFloorplanGuestCounts,
    getOrderedFloorplanReservationProfiles,
    getPrimaryProfile,
    getReservationDisplay,
} from "../utils/reservation-utils";
import {useViewportStore} from "../hooks/useViewportControls";
import FloorplanReservationStatusSelect from "./FloorplanReservationStatusSelect";
import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";

interface Props {
    children: ReactNode;
    reservationItem: FloorplanReservationItemDTO;
    tableName?: string;
    disabled?: boolean;
    active: boolean;
    onOpen: () => void;
    onClose: () => void;
    onOpenReservation?: () => void;
}

interface InfoRowProps {
    label: string;
    children: ReactNode;
}

interface InlineDetailProps {
    icon: ReactNode;
    children: ReactNode;
}

const TOOLTIP_CLOSE_DELAY_MS = 120;
const DOUBLE_TAP_DELAY_MS = 350;
const POINTER_INTERACTION_MAX_AGE_MS = 1000;
const TOOLTIP_WIDTH = 280;

const InlineDetail = ({icon, children}: InlineDetailProps) => (
    <Box
        component="span"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            maxWidth: "100%",
            minWidth: 0,
        }}
    >
        <Box component="span" sx={{display: "inline-flex", flexShrink: 0}}>
            {icon}
        </Box>
        <Typography
            component="span"
            sx={{
                color: "text.secondary",
                fontSize: "0.8rem",
                lineHeight: 1.3,
                overflowWrap: "anywhere",
            }}
        >
            {children}
        </Typography>
    </Box>
);

const InfoRow = ({label, children}: InfoRowProps) => (
    <Stack
        direction="row"
        sx={{
            columnGap: 1.25,
            px: 1.5,
            py: 0.75,
        }}
    >
        <Typography
            sx={{
                width: 76,
                flexShrink: 0,
                pt: "2px",
                color: "text.secondary",
                fontSize: "0.66rem",
                fontWeight: 700,
                lineHeight: 1.25,
                letterSpacing: "0.04em",
                overflowWrap: "anywhere",
            }}
        >
            {label.toUpperCase()}
        </Typography>
        <Box
            sx={{
                flex: 1,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                minWidth: 0,
            }}
        >
            {children}
        </Box>
    </Stack>
);

const FloorplanReservationTooltipContent = ({
                                                 reservationItem,
                                                 tableName,
                                                 onClose,
                                                 onOpenReservation,
                                                 onStatusMenuOpen,
                                                 onStatusMenuClose,
                                             }: {
    reservationItem: FloorplanReservationItemDTO;
    tableName?: string;
    onClose: () => void;
    onOpenReservation?: () => void;
    onStatusMenuOpen: () => void;
    onStatusMenuClose: () => void;
}) => {
    const {t, tFallback, localize} = useLocalize();
    const noteGroupIndex = useNoteGroupIndex();
    const reservation = reservationItem.restaurantReservation;
    const displayGuestCounts = getFloorplanGuestCounts(reservationItem);
    const service = useService(reservation.serviceId);
    const {
        guestNames,
        guestCount,
        roomNames,
        timeRange,
        status,
        statusText,
        fromBookingEngine,
        isBirthday,
        preferences,
        start,
    } = getReservationDisplay({
        reservation,
        profiles: reservationItem.profiles,
        ageCategories: reservationItem.ageCategories,
        tableResources: reservationItem.tableResources,
        accommodationResources: reservationItem.accommodationResources,
        guestCounts: displayGuestCounts,
    });

    const displayedProfiles = getOrderedFloorplanReservationProfiles(reservationItem);
    const primaryProfile = getPrimaryProfile(reservation, displayedProfiles)
        ?? displayedProfiles[0];
    const profileNames = displayedProfiles
        .map(profile => ({
            id: profile.id,
            name: getProfileFullNameReversed(profile, ""),
            primary: profile.id === primaryProfile?.id,
        }))
        .filter(profile => Boolean(profile.name));

    const headerBackgroundColor = getEventColorBasedOnStatus(status) || "#5f6b7a";
    const headerTextColor = getContrastingColor(headerBackgroundColor) || "#ffffff";
    const isSerial = Boolean(reservation.reservationGroupId);
    const headerGuestName = getProfileFullNameReversed(primaryProfile, guestNames || t("Guest"));
    const reservationDate = formatFullDMY(start);
    const headerSubtitle = [
        [reservationDate, timeRange !== "-" ? timeRange : ""].filter(Boolean).join(" · "),
        tableName,
    ].filter(Boolean).join(" · ");
    const serviceName = localize(service?.nameLocalized) || service?.name || "";
    const noteEntry = reservation.notes
        ?.find(reservationNote => Boolean(reservationNote.note?.trim()));
    const note = noteEntry?.note?.trim();

    const preferenceDetails: Array<{
        key: string;
        Icon: React.ElementType;
        color: string;
        label: string;
    }> = [];
    const seenPreferences = new Set<string>();
    for (const preference of preferences) {
        const type = preference.preferenceType;
        const value = preference.preferenceValue?.trim();
        const iconMapping = type ? ALLERGY_ICON_MAP[type] : undefined;
        if (!type || !value || !iconMapping) continue;

        const key = `${type}-${value}`;
        if (seenPreferences.has(key)) continue;
        seenPreferences.add(key);
        preferenceDetails.push({
            key,
            Icon: iconMapping.icon,
            color: iconMapping.color,
            label: tFallback(`${type}_${value}`, labelizeUnderScoreText(value)),
        });
    }

    const sections: Array<{key: string; content: ReactNode}> = [];

    if (profileNames.length > 1) {
        sections.push({
            key: "guests",
            content: (
                <InfoRow label={t("Guests")}>
                    <Stack gap={0.25} width="100%">
                        {profileNames.map((profile, index) => (
                            <Typography
                                key={profile.id ?? index}
                                sx={{
                                    color: "text.secondary",
                                    fontSize: "0.8rem",
                                    fontWeight: profile.primary ? 700 : 400,
                                    lineHeight: 1.3,
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {profile.name}
                            </Typography>
                        ))}
                    </Stack>
                </InfoRow>
            ),
        });
    }

    if (roomNames || guestCount > 0) {
        sections.push({
            key: "stay",
            content: (
                <InfoRow label={t("Stay")}>
                    {roomNames && (
                        <InlineDetail
                            icon={<HotelOutlinedIcon sx={{fontSize: 15, color: "#3B82F6"}}/>}
                        >
                            {roomNames}
                        </InlineDetail>
                    )}
                    {guestCount > 0 && (
                        <AgeCategoriesElement
                            ageCategories={reservationItem.ageCategories}
                            ageCategoryCounts={displayGuestCounts}
                            aliasAgeCategories={reservationItem.aliasAgeCategories}
                            totalGuests={guestCount}
                            variant="inline"
                        />
                    )}
                </InfoRow>
            ),
        });
    }

    if (serviceName) {
        sections.push({
            key: "service",
            content: (
                <InfoRow label={t("Service")}>
                    <Typography
                        sx={{
                            overflowWrap: "anywhere",
                            fontSize: "0.8rem",
                            color: "text.secondary",
                        }}
                    >
                        {serviceName}
                    </Typography>
                </InfoRow>
            ),
        });
    }

    if (preferenceDetails.length > 0) {
        sections.push({
            key: "preferences",
            content: (
                <InfoRow label={t("Preferences")}>
                    {preferenceDetails.map(({key, Icon, color, label}) => (
                        <InlineDetail
                            key={key}
                            icon={<Icon sx={{fontSize: 15, color}}/>}
                        >
                            {label}
                        </InlineDetail>
                    ))}
                </InfoRow>
            ),
        });
    }

    if (note) {
        sections.push({
            key: "note",
            content: (
                <InfoRow label={t("Note")}>
                    <Stack direction="row" alignItems="flex-start" gap={0.5}>
                        <NoteGroupIcon note={noteEntry} index={noteGroupIndex} fontSize={13}/>
                        <Typography
                            sx={{
                                minWidth: 0,
                                display: "-webkit-box",
                                overflow: "hidden",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 3,
                                fontSize: "0.8rem",
                                fontStyle: "italic",
                                color: "text.secondary",
                            }}
                        >
                            {`"${note}"`}
                        </Typography>
                    </Stack>
                </InfoRow>
            ),
        });
    }

    return (
        <Paper
            elevation={8}
            sx={{
                width: TOOLTIP_WIDTH,
                maxWidth: "calc(100vw - 16px)",
                overflow: "hidden",
                borderRadius: 1,
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    backgroundColor: headerBackgroundColor,
                    color: headerTextColor,
                    px: 1.5,
                    py: 1,
                }}
            >
                <IconButton
                    aria-label={t("Close")}
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 3,
                        right: 3,
                        width: 36,
                        height: 36,
                        color: headerTextColor,
                        opacity: 0.85,
                        backgroundColor: "transparent",
                        "&:hover": {
                            opacity: 1,
                            backgroundColor: "rgba(255,255,255,0.08)",
                        },
                        "&:focus-visible": {
                            opacity: 1,
                            outline: `2px solid ${headerTextColor}`,
                            outlineOffset: -4,
                        },
                    }}
                >
                    <CloseIcon sx={{fontSize: 16}}/>
                </IconButton>

                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{columnGap: 0.5, minHeight: 20, pr: 3.5}}
                >
                    <Typography
                        sx={{
                            fontSize: "0.69rem",
                            fontWeight: 700,
                            letterSpacing: "0.05em",
                        }}
                    >
                        {t(`Reservation status ${status}`, {defaultValue: statusText}).toUpperCase()}
                    </Typography>
                    {isSerial && (
                        <Chip
                            size="small"
                            icon={<CachedIcon sx={{fontSize: 13, color: `${headerTextColor} !important`}}/>}
                            label={t("Slot type SERIAL").toUpperCase()}
                            sx={{
                                height: 20,
                                color: headerTextColor,
                                backgroundColor: "rgba(255,255,255,0.22)",
                                "& .MuiChip-label": {
                                    px: 0.625,
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.04em",
                                },
                            }}
                        />
                    )}
                </Stack>

                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{columnGap: 0.5, mt: 0.125, pr: 3.5}}
                >
                    <Typography
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "1rem",
                            fontWeight: 700,
                            lineHeight: 1.3,
                        }}
                    >
                        {headerGuestName}
                    </Typography>
                    {primaryProfile?.vipCode && <StarIcon sx={{fontSize: 16, flexShrink: 0}}/>}
                    {isBirthday && <CakeOutlined sx={{fontSize: 16, flexShrink: 0}}/>}
                    {fromBookingEngine && <BookOnlineIcon sx={{fontSize: 16, flexShrink: 0}}/>}
                </Stack>

                {headerSubtitle && (
                    <Typography
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontSize: "0.76rem",
                            opacity: 0.9,
                            mt: 0.125,
                        }}
                    >
                        {headerSubtitle}
                    </Typography>
                )}
            </Box>

            {sections.length > 0 && (
                <Stack
                    divider={<Divider/>}
                    sx={{maxHeight: "55vh", overflowY: "auto"}}
                >
                    {sections.map(section => (
                        <React.Fragment key={section.key}>
                            {section.content}
                        </React.Fragment>
                    ))}
                </Stack>
            )}

            <Divider/>

            <Stack
                direction="row"
                alignItems="stretch"
                sx={{gap: 1, px: 1.5, py: 0.875}}
            >
                <FloorplanReservationStatusSelect
                    reservationItem={reservationItem}
                    onOpen={onStatusMenuOpen}
                    onClose={onStatusMenuClose}
                    sx={{
                        flex: "1 1 0",
                        minWidth: 0,
                        height: 32,
                        "& .MuiSelect-select": {
                            py: 0.5,
                            pl: 1,
                            pr: "28px !important",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "0.76rem",
                        },
                    }}
                />

                {onOpenReservation && (
                    <Button
                        aria-label={t("Open")}
                        variant="outlined"
                        startIcon={<OpenInNewIcon sx={{fontSize: "16px !important"}}/>}
                        onClick={() => {
                            onClose();
                            onOpenReservation();
                        }}
                        sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                            height: 32,
                            px: 1,
                            borderColor: "divider",
                            color: "text.secondary",
                            fontSize: "0.76rem",
                            fontWeight: 600,
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            "& .MuiButton-startIcon": {mr: 0.5},
                            "&:hover": {
                                borderColor: "text.secondary",
                                backgroundColor: "action.hover",
                            },
                        }}
                    >
                        {t("Open")}
                    </Button>
                )}
            </Stack>
        </Paper>
    );
};

const FloorplanReservationTooltip = ({
                                         children,
                                         reservationItem,
                                         tableName,
                                         disabled = false,
                                         active,
                                         onOpen,
                                         onClose,
                                         onOpenReservation,
                                     }: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const statusMenuOpenRef = useRef(false);
    const lastPointerInteractionRef = useRef<{type: string; timestamp: number}>();
    const lastTouchTapRef = useRef<number>();
    const zoomScale = useViewportStore(state => state.zoom) / 100;

    const clearCloseTimer = useCallback(() => {
        if (!closeTimerRef.current) return;
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = undefined;
    }, []);

    const closeTooltip = useCallback(() => {
        clearCloseTimer();
        statusMenuOpenRef.current = false;
        lastPointerInteractionRef.current = undefined;
        lastTouchTapRef.current = undefined;
        setAnchorEl(null);
        onClose();
    }, [clearCloseTimer, onClose]);

    const scheduleTooltipClose = useCallback(() => {
        clearCloseTimer();
        if (statusMenuOpenRef.current) return;
        closeTimerRef.current = setTimeout(closeTooltip, TOOLTIP_CLOSE_DELAY_MS);
    }, [clearCloseTimer, closeTooltip]);

    const handleStatusMenuOpen = useCallback(() => {
        statusMenuOpenRef.current = true;
        clearCloseTimer();
    }, [clearCloseTimer]);

    const handleStatusMenuClose = useCallback(() => {
        statusMenuOpenRef.current = false;
        clearCloseTimer();
    }, [clearCloseTimer]);

    useEffect(() => clearCloseTimer, [clearCloseTimer]);

    useEffect(() => {
        if (active) return;
        clearCloseTimer();
        statusMenuOpenRef.current = false;
        lastPointerInteractionRef.current = undefined;
        lastTouchTapRef.current = undefined;
        setAnchorEl(null);
    }, [active, clearCloseTimer]);

    useEffect(() => {
        if (disabled) closeTooltip();
    }, [closeTooltip, disabled]);

    useEffect(() => {
        if (disabled || !active || !anchorEl) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            event.stopPropagation();
            closeTooltip();
        };

        document.addEventListener("keydown", handleEscape, true);
        return () => document.removeEventListener("keydown", handleEscape, true);
    }, [active, anchorEl, closeTooltip, disabled]);

    return (
        <>
            <Box
                onMouseEnter={event => {
                    if (disabled) return;
                    clearCloseTimer();
                    setAnchorEl(event.currentTarget);
                    onOpen();
                }}
                onMouseLeave={scheduleTooltipClose}
                onFocus={event => {
                    if (disabled) return;
                    clearCloseTimer();
                    setAnchorEl(event.currentTarget);
                    onOpen();
                }}
                onBlur={scheduleTooltipClose}
                onPointerDownCapture={event => {
                    if (disabled) return;
                    lastPointerInteractionRef.current = {
                        type: event.pointerType,
                        timestamp: Date.now(),
                    };
                }}
                onClickCapture={event => {
                    const now = Date.now();
                    const pointerInteraction = lastPointerInteractionRef.current;
                    lastPointerInteractionRef.current = undefined;
                    const isTouchInteraction = (pointerInteraction?.type === "touch"
                            || pointerInteraction?.type === "pen")
                        && now - pointerInteraction.timestamp <= POINTER_INTERACTION_MAX_AGE_MS;

                    if (!isTouchInteraction) {
                        closeTooltip();
                        return;
                    }

                    event.preventDefault();
                    event.stopPropagation();

                    const previousTap = lastTouchTapRef.current;
                    const isDoubleTap = previousTap !== undefined
                        && now - previousTap <= DOUBLE_TAP_DELAY_MS;

                    if (isDoubleTap && onOpenReservation) {
                        closeTooltip();
                        onOpenReservation();
                        return;
                    }

                    lastTouchTapRef.current = now;
                    clearCloseTimer();
                    setAnchorEl(event.currentTarget);
                    onOpen();
                }}
                sx={{display: "inline-flex"}}
            >
                {children}
            </Box>

            <Popper
                open={!disabled && active && Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom"
                modifiers={[
                    {
                        name: "offset",
                        options: {offset: [0, 8 * zoomScale]},
                    },
                    {
                        name: "preventOverflow",
                        options: {padding: 8},
                    },
                ]}
                sx={{zIndex: theme => theme.zIndex.tooltip}}
            >
                <Box
                    role="tooltip"
                    onMouseEnter={clearCloseTimer}
                    onMouseLeave={scheduleTooltipClose}
                    sx={{
                        transform: `scale(${zoomScale})`,
                        transformOrigin: "top center",
                    }}
                >
                    <FloorplanReservationTooltipContent
                        reservationItem={reservationItem}
                        tableName={tableName}
                        onClose={closeTooltip}
                        onOpenReservation={onOpenReservation}
                        onStatusMenuOpen={handleStatusMenuOpen}
                        onStatusMenuClose={handleStatusMenuClose}
                    />
                </Box>
            </Popper>
        </>
    );
};

export default FloorplanReservationTooltip;
